/**
 * Value Object: Duration
 * Represents a time duration with validation and unit conversion
 */
export class Duration {
  private readonly _seconds: number;

  constructor(seconds: number) {
    if (seconds < 0) {
      throw new Error('Duration cannot be negative');
    }
    this._seconds = seconds;
  }

  static fromSeconds(seconds: number): Duration {
    return new Duration(seconds);
  }

  static fromMinutes(minutes: number): Duration {
    return new Duration(minutes * 60);
  }

  static fromHours(hours: number): Duration {
    return new Duration(hours * 3600);
  }

  static fromTimeString(timeString: string): Duration {
    // Format: "HH:MM:SS" or "MM:SS"
    const parts = timeString.split(':').map(Number);
    
    if (parts.length === 2) {
      // MM:SS format
      const [minutes, seconds] = parts;
      return new Duration(minutes * 60 + seconds);
    } else if (parts.length === 3) {
      // HH:MM:SS format
      const [hours, minutes, seconds] = parts;
      return new Duration(hours * 3600 + minutes * 60 + seconds);
    } else {
      throw new Error('Invalid time format. Use HH:MM:SS or MM:SS');
    }
  }

  get seconds(): number {
    return this._seconds;
  }

  get minutes(): number {
    return this._seconds / 60;
  }

  get hours(): number {
    return this._seconds / 3600;
  }

  get totalMinutes(): number {
    return Math.floor(this._seconds / 60);
  }

  get totalHours(): number {
    return Math.floor(this._seconds / 3600);
  }

  get remainingSeconds(): number {
    return this._seconds % 60;
  }

  get remainingMinutes(): number {
    return Math.floor((this._seconds % 3600) / 60);
  }

  equals(other: Duration): boolean {
    return this._seconds === other._seconds;
  }

  add(other: Duration): Duration {
    return new Duration(this._seconds + other._seconds);
  }

  subtract(other: Duration): Duration {
    const result = this._seconds - other._seconds;
    if (result < 0) {
      throw new Error('Result cannot be negative');
    }
    return new Duration(result);
  }

  multiply(factor: number): Duration {
    if (factor < 0) {
      throw new Error('Factor cannot be negative');
    }
    return new Duration(this._seconds * factor);
  }

  divide(other: Duration): number {
    if (other._seconds === 0) {
      throw new Error('Cannot divide by zero duration');
    }
    return this._seconds / other._seconds;
  }

  isGreaterThan(other: Duration): boolean {
    return this._seconds > other._seconds;
  }

  isLessThan(other: Duration): boolean {
    return this._seconds < other._seconds;
  }

  toTimeString(): string {
    const hours = this.totalHours;
    const minutes = this.remainingMinutes;
    const seconds = this.remainingSeconds;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  }

  toString(unit: 'seconds' | 'minutes' | 'hours' = 'minutes'): string {
    switch (unit) {
      case 'seconds':
        return `${this._seconds.toFixed(0)}s`;
      case 'minutes':
        return `${this.minutes.toFixed(1)}min`;
      case 'hours':
        return `${this.hours.toFixed(2)}h`;
      default:
        return this.toTimeString();
    }
  }
}
